import { useParams } from 'react-router-dom';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
} from '@mui/material';
import { useAxios, useUi } from 'hooks';

type PerguntaFormProps = {
  open: boolean;
  handleClose: () => void;
  idBeneficiario: string;
  peso: string;
  altura: string;
  imc: string;
  idImc: string;
  createNewDoenca: boolean;
};

type FormValues = {
  pergunta: string;
  liberarAnexo: boolean;
};

const MIN_LENGTH_INPUT_TEXT = 10;
const MAX_LENGTH_INPUT_TEXT = 300;

const schema = yup.object().shape({
  pergunta: yup
    .string()
    .min(
      MIN_LENGTH_INPUT_TEXT,
      `O tamanho mínimo é de ${MIN_LENGTH_INPUT_TEXT} caracteres`
    )
    .max(
      MAX_LENGTH_INPUT_TEXT,
      `O tamanho máximo é de ${MAX_LENGTH_INPUT_TEXT} caracteres`
    )
    .required('Este campo é obrigatório'),
  liberarAnexo: yup.boolean(),
});

const SolicitarInfoImc: React.FC<PerguntaFormProps> = ({
  open,
  handleClose,
  idBeneficiario,
  peso,
  altura,
  imc,
  idImc,
  createNewDoenca,
}) => {
  const { errorMessage, successMessage } = useUi();
  const { api } = useAxios();
  const { protocolo } = useParams();
  const { control, formState, handleSubmit, reset } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      pergunta: '',
      liberarAnexo: true,
    },
  });

  const onClose = (): void => {
    reset();
    handleClose();
  };

  const savePergunta = (
    pergunta: string,
    liberarAnexo: boolean,
    idDoenca: string | undefined
  ): void => {
    api
      .post(`${process.env.REACT_APP_MS_AUDITORIA_URL}/perguntas`, {
        protocolo,
        pergunta,
        liberarAnexo,
        idBeneficiario,
        idDoenca,
      })
      .then(() => {
        successMessage('A sua pendência foi registrada.');
        onClose();
      })
      .catch(error => {
        if (!error.response) {
          errorMessage('Não foi possível registrar a pendência.');
        }
        if (error.response.status === 400) {
          const { message } = error.response.data.errorMessage[0];
          errorMessage(message);
        }
      });
  };

  const onSubmit: SubmitHandler<FormValues> = ({ pergunta, liberarAnexo }) => {
    if (!createNewDoenca) {
      savePergunta(pergunta, liberarAnexo, idImc);
      return;
    }

    api
      .post(`${process.env.REACT_APP_MS_AUDITORIA_URL}/doencas/imc-alterado`, {
        protocolo,
        idBeneficiario,
        peso,
        altura,
        imc,
      })
      .then(({ data }) => {
        const id = data.idDoenca;
        savePergunta(pergunta, liberarAnexo, id);
      })
      .catch(error => {
        if (!error.response) {
          errorMessage('Não foi possível registrar alerta de IMC.');
        }
        if (error.response.status === 400) {
          const { message } = error.response.data.errorMessage[0];
          errorMessage(message);
        }
      });
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>Solicitar informações adicionais</DialogTitle>
      <DialogContent>
        <form>
          <Grid container sx={{ '& > :not(style)': { mb: 2 } }}>
            <Grid item xs={12} sx={{ pt: 1 }}>
              <Controller
                control={control}
                name="pergunta"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    inputProps={{ maxLength: MAX_LENGTH_INPUT_TEXT }}
                    variant="outlined"
                    label="Descreva sua pergunta"
                    error={!!formState.errors.pergunta}
                    helperText={`${field.value.length}/${MAX_LENGTH_INPUT_TEXT}`}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                error={!!formState.errors.liberarAnexo}
              >
                <FormLabel component="legend">
                  Documentação do proponente
                </FormLabel>

                <FormControlLabel
                  label="Permitir anexo"
                  control={
                    <Controller
                      control={control}
                      name="liberarAnexo"
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  }
                  title="Solicitar documentação do proponente"
                />
                {!!formState.errors.liberarAnexo && (
                  <FormHelperText>
                    {formState.errors.liberarAnexo.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={!formState.isValid}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SolicitarInfoImc;
